import { createPinia } from "pinia";
import { createApp } from "vue";

import App from "./App.vue";
import { router } from "./router";
import { useAuthStore } from "./stores/auth";

import "./style.css";

const app = createApp(App);
app.use(createPinia());
app.use(router);

// dynamic import が失敗 (古いハッシュ参照で 404 など) したら、
// SW + キャッシュを全クリアして 1 度だけ強制リロード。
const RELOAD_MARK = "gwiki.chunk_reload";
async function selfHealAndReload(reason: string) {
  if (sessionStorage.getItem(RELOAD_MARK)) {
    console.error("[self-heal] already reloaded once, giving up:", reason);
    return;
  }
  sessionStorage.setItem(RELOAD_MARK, "1");
  console.warn("[self-heal] purging caches and reloading:", reason);
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } finally {
    location.reload();
  }
}

router.onError((err) => {
  const msg = String((err as Error)?.message ?? err);
  if (/Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed/i.test(msg)) {
    selfHealAndReload(`router import error: ${msg}`);
  }
});

// 起動時にローカルストレージのトークンで自身を復元。
// router.beforeEach は auth.ready() を await するので mount 前後どちらでも安全。
const auth = useAuthStore();
auth.restore();
app.mount("#app");
sessionStorage.removeItem(RELOAD_MARK);

// Service Worker 登録 (本番ビルド時のみ、HTTPS 必須)
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((reg) => {
        // ロード毎に SW 更新チェック (HTTP キャッシュをバイパス)
        reg.update().catch(() => {});

        const promote = (w: ServiceWorker | null) => {
          if (!w) return;
          w.addEventListener("statechange", () => {
            if (w.state === "installed" && navigator.serviceWorker.controller) {
              w.postMessage("SKIP_WAITING");
            }
          });
        };
        promote(reg.waiting);
        reg.addEventListener("updatefound", () => promote(reg.installing));

        // 新 SW が controller になった瞬間に 1 度だけリロード
        let reloaded = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (reloaded) return;
          reloaded = true;
          window.location.reload();
        });
      })
      .catch((err) => console.error("[SW] registration failed:", err));
  });
}
